import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken } from './theme';
import { randomStr } from '../../Shared';
import { focusMaskClass } from '../../Shared/a11yUtils';
const componentName = 'Select';
export const optionClasses = {
    root: `ui-Select-option-root-${randomStr()}`,
    disabled: `ui-Select-option-disabled`,
    selected: `ui-Select-option-selected`,
    text: `ui-Select-option-text`,
    icon: `ui-Select-option-icon`,
    iconPlaceholder: `ui-Select-option-iconPlaceholder`,
};
export const inputClasses = {
    root: `ui-Select-input-root-${randomStr()}`,
    disabled: `ui-Select-input-disabled`,
    focused: `ui-Select-input-focused`,
    error: `ui-Select-input-error`,
};
export const selectClasses = {
    root: `ui-Select-root`,
    select: `ui-Select-select`,
    clearable: `ui-Select-clearable`,
    list: `ui-Select-list`,
    paper: `ui-Select-paper`,
    icon: `ui-Select-icon`,
    disabled: `ui-Select-disabled`,
    noData: 'ui-Select-no-data',
    clearGroup: 'ui-Select-clear-group',
    clearButton: 'ui-Select-clear-button',
    optionRoot: optionClasses.root,
    optionDisabled: optionClasses.disabled,
    optionSelected: optionClasses.selected,
    optionText: optionClasses.text,
    optionIcon: optionClasses.icon,
    optionIconPlaceholder: optionClasses.iconPlaceholder,
    inputRoot: inputClasses.root,
    inputDisabled: inputClasses.disabled,
    inputFocused: inputClasses.focused,
    inputError: inputClasses.error,
};
export function getStyle(theme, opts) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        [`& > .${selectClasses.select}`]: {
            padding: tk(token.padding[opts.size]),
            // Temporarily hide height-handling rendering flicker issues.
            // The different size heights of Select will be controlled by the css height of the Input below.
            lineHeight: `${tk(token.height[opts.size])}px !important`,
            height: 'auto',
            '.notranslate::after': opts.placeholder
                ? {
                    content: `"${opts.placeholder}"`,
                    color: tk(token.color.secondary.placeholder),
                }
                : {},
            [`&.${selectClasses.disabled}`]: {
                color: token.color.secondary.disabled,
                textFillColor: token.color.secondary.disabled,
                '.notranslate::after': opts.placeholder
                    ? {
                        color: tk(token.color.secondary.disabled),
                    }
                    : {},
            },
            [`div&.${selectClasses.clearable}`]: {
                paddingRight: '56px',
            },
        },
        [`.${selectClasses.icon}`]: {
            display: 'flex',
            right: tk(token.iconRight[opts.size]),
            fontSize: tk(token.iconSize[opts.size]),
            color: tk(token.color.menuText),
            transform: 'rotate(90deg)',
            transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            [`&.MuiSelect-iconOpen`]: {
                transform: 'rotate(270deg)',
            },
        },
        [`& .${selectClasses.list}`]: {
            overflow: 'hidden auto',
            padding: '8px',
            background: tk(token.background.secondary.default),
            maxHeight: `${opts.listMaxHeight ?? 256}px`,
        },
        [`& .${selectClasses.paper}`]: {
            borderRadius: tk(token.borderRadius[opts.size]),
            marginTop: '4px',
            boxShadow: tk(token.boxShadow.pager),
            fontFamily: tk(token.fontFamily),
            border: `0.5px solid ${tk(token.border.secondary.paper)} !important`,
            // Here the width of 0 will be increased by the min-width defined by MUI, which equals to the width of input box
            width: opts.longOptionsDisplay === 'multiline' ||
                opts.longOptionsDisplay === 'trim'
                ? 0
                : undefined,
        },
        [`.${selectClasses.noData}`]: {
            fontFamily: tk(token.fontFamily),
            textAlign: 'center',
            padding: '6px 8px',
            fontSize: tk(token.fontSize[opts.size]),
            lineHeight: '20px',
            color: tk(token.color.emptyText),
            pointerEvents: 'none',
        },
        [`.${selectClasses.clearGroup}`]: {
            pointerEvents: 'none',
        },
        [`.${selectClasses.clearButton}`]: {
            pointerEvents: 'initial',
            position: 'absolute',
            top: 'calc(50% - 12px)',
            right: tk(token.iconRight[opts.size] + token.iconSize[opts.size] + 4),
        },
    };
    return style;
}
export function getInputStyle(theme, opts) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        [`&.${inputClasses.root}`]: {
            height: `${tk(token.height[opts.size])}px !important`,
            border: `1px solid ${tk(token.border.secondary.default)}`,
            borderRadius: tk(token.borderRadius[opts.size]),
            fontSize: tk(token.fontSize[opts.size]),
            color: tk(token.color.text),
            fontFamily: tk(token.fontFamily),
            background: tk(token.background.default),
            [`&:hover:not(.${inputClasses.disabled})`]: {
                background: tk(token.background.secondary.hover),
            },
            [`&:active:not(.${inputClasses.disabled})`]: {
                background: tk(token.background.secondary.active),
            },
            [`&:active:has(.${selectClasses.clearButton}:active)`]: {
                background: tk(token.background.secondary.hover),
            },
            [`&.${inputClasses.disabled}`]: {
                borderColor: token.border.secondary.disabled,
                [`&.${inputClasses.error}`]: {
                    borderColor: token.border.secondary.errorDisabled,
                },
                [`.${selectClasses.icon}`]: {
                    color: tk(token.color.secondary.disabled),
                },
            },
        },
        [`html.${focusMaskClass} &.${inputClasses.focused}, body.${focusMaskClass} &.${inputClasses.focused}`]: {
            outline: tk(token.outline.focus),
            outlineOffset: tk(token.outlineOffset),
        },
        [`&.${inputClasses.error}`]: {
            border: `1px solid ${tk(token.border.danger.default)}`,
        },
    };
    return style;
}
export function getOptionStyle(theme, opts) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        [`&.${optionClasses.root}`]: {
            paddingTop: 0,
            paddingBottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: '32px',
            padding: '6px 8px',
            borderRadius: '4px',
            fontSize: tk(token.fontSize[opts.size]),
            lineHeight: '20px',
            color: tk(token.color.menuText),
            whiteSpace: opts.multiline ? 'wrap' : 'nowrap',
            '&:hover, &:focus-visible': {
                background: tk(token.background.secondary.hover),
            },
            '&:active': {
                background: tk(token.background.secondary.active),
            },
        },
        [`html.${focusMaskClass} &.${optionClasses.root}:focus-visible, body.${focusMaskClass} &.${optionClasses.root}:focus-visible`]: {
            outline: tk(token.outline.focus),
        },
        [`&.${optionClasses.disabled}`]: {
            color: tk(token.color.secondary.disabled),
            opacity: 1,
            cursor: 'not-allowed',
        },
        [`&.${optionClasses.selected}`]: {
            background: 'transparent',
        },
        [`.${optionClasses.text}`]: {
            flex: '1 0 0',
            minWidth: 0,
        },
        [`.${optionClasses.icon}`]: {
            flex: 'none',
            color: tk(token.color.primary.default),
            lineHeight: 1,
            paddingLeft: '8px',
            fontSize: tk(token.iconSize[opts.size]),
        },
        [`.${optionClasses.iconPlaceholder}`]: {
            display: 'inline-block',
            width: '1em',
            height: '1em',
        },
    };
    return style;
}
